import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/core/components/ui/select";
import { TenantOption } from "src/core/types/tenant";

type TenantSelectProps = {
  tenantId: string | null;
  tenants: TenantOption[];
  onChange: (tenantId: string) => void;
  isSwitching?: boolean; // Changement de tenant via la liste déroulante
};

export const TenantSelect = ({
  tenantId,
  tenants,
  onChange,
  isSwitching = false,
}: TenantSelectProps) => {
  const { t } = useTranslation();

  const disabled = tenants.length <= 1 || isSwitching;

  const currentTenant = useMemo(() => {
    if (!tenantId) return null;

    return tenants.find((tenant) => tenant.id === tenantId) ?? null;
  }, [tenants, tenantId]);

  const label = currentTenant?.name ?? tenantId ?? t("tenant.select");

  return (
    <Select
      value={tenantId ?? undefined}
      onValueChange={(val) => {
        if (!val) return;
        if (val === tenantId) return;
        if (isSwitching) return;
        onChange(val);
      }}
      disabled={disabled}
    >
      <SelectTrigger data-testid="tenant-select-trigger" className="select-tenant-trigger  ">
        <SelectValue placeholder={t("tenant.select")} className="w-full text-right">
          {label}
        </SelectValue>
      </SelectTrigger>

      <SelectContent className="select-tenant-content">
        {tenants
          .filter((tenant) => tenant.id !== tenantId)
          .map((tenant) => (
            <SelectItem
              key={tenant.id}
              value={tenant.id}
              data-testid={`tenant-option-${tenant.id}`}
              className="select-tenant-item"
            >
              {tenant.name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
};
